import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

const PDF_SCALE = 2

const waitForImage = (img: HTMLImageElement) => {
  if (img.complete && img.naturalWidth > 0) return Promise.resolve()

  return new Promise<void>((resolve) => {
    const done = () => resolve()
    img.addEventListener('load', done, { once: true })
    img.addEventListener('error', done, { once: true })
  })
}

const getRotationDeg = (transform: string) => {
  if (!transform || transform === 'none') return 0

  const rotateMatch = transform.match(/rotate\(([-.\d]+)deg\)/)
  if (rotateMatch) return Number(rotateMatch[1])

  const matrixMatch = transform.match(/matrix\(([^)]+)\)/)
  if (!matrixMatch) return 0

  const [a, b] = matrixMatch[1].split(',').map(Number)
  return Math.round(Math.atan2(b, a) * (180 / Math.PI))
}

const loadImage = async (src: string) => {
  const image = new Image()
  image.crossOrigin = 'anonymous'
  image.src = src
  await waitForImage(image)
  return image
}

const rasterizeSvgAtSize = async (src: string, width: number, height: number) => {
  const svgText = await fetch(src).then((response) => {
    if (!response.ok) throw new Error('Failed to fetch SVG')
    return response.text()
  })

  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml')
  const svg = doc.documentElement
  svg.setAttribute('width', String(width))
  svg.setAttribute('height', String(height))

  const blob = new Blob([new XMLSerializer().serializeToString(svg)], {
    type: 'image/svg+xml;charset=utf-8'
  })
  const objectUrl = URL.createObjectURL(blob)

  try {
    return await loadImage(objectUrl)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

const rasterizeImage = async (img: HTMLImageElement, scale: number) => {
  const src = img.currentSrc || img.src
  if (!src) return null

  const displayWidth = Math.max(img.clientWidth, 1)
  const displayHeight = Math.max(img.clientHeight, 1)
  const targetW = Math.round(displayWidth * scale)
  const targetH = Math.round(displayHeight * scale)
  const isSvg = /\.svg(\?|#|$)/i.test(src.split('?')[0])

  const source = isSvg
    ? await rasterizeSvgAtSize(src, targetW, targetH).catch(() => loadImage(src))
    : await loadImage(src)

  if (!source.naturalWidth) return null

  const rotation = getRotationDeg(getComputedStyle(img).transform)
  const canvas = document.createElement('canvas')
  canvas.width = targetW
  canvas.height = targetH
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  if (rotation) {
    ctx.translate(targetW / 2, targetH / 2)
    ctx.rotate((rotation * Math.PI) / 180)
    ctx.drawImage(source, -targetW / 2, -targetH / 2, targetW, targetH)
  } else {
    ctx.drawImage(source, 0, 0, targetW, targetH)
  }

  return canvas.toDataURL('image/png')
}

export const useContractPdf = () => {
  const generatePdf = async () => {
    const pages = document.querySelectorAll('.a4-page')

    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      compress: true
    })

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i] as HTMLElement
      const images = Array.from(page.querySelectorAll('img'))
      await Promise.all(images.map(waitForImage))

      const originals = await Promise.all(
        images.map(async (img) => {
          const src = img.getAttribute('src')
          const styleTransform = img.style.transform
          const dataUrl = await rasterizeImage(img, PDF_SCALE)
          if (dataUrl) {
            img.src = dataUrl
            img.style.transform = 'none'
            await waitForImage(img)
          }
          return { img, src, styleTransform }
        })
      )

      try {
        const canvas = await html2canvas(page, {
          scale: PDF_SCALE,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#fff',
          imageTimeout: 15000
        })

        const imgData = canvas.toDataURL('image/jpeg', 0.98)

        if (i > 0) pdf.addPage()

        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297)
      } finally {
        originals.forEach(({ img, src, styleTransform }) => {
          if (src) img.src = src
          img.style.transform = styleTransform
        })
      }
    }

    pdf.save('contract.pdf')
  }

  return { generatePdf }
}
